import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Search, SlidersHorizontal, MapPin, Banknote, Maximize2,
  DoorOpen, ChevronRight, Heart, Bed, Bath, Square,
  Zap, TrendingUp, Shield, Eye, Building2, Hammer,
  Home, Star, Flame, Bot
} from 'lucide-react';
import HOPPyAIChat from '../components/hopphomes/HOPPyAIChat';
import HotDealsSection from '../components/secretdeals/HotDealsSection';

/* ─── Property card data ─────────────────────────────── */
const FEATURED = [
  {
    id: 1,
    badge: 'KIVÁLÓ AJÁNLAT',
    badgeColor: 'bg-[#e31937]',
    title: 'Panorámás penthouse',
    location: 'Budapest, XII. kerület',
    price: '89 900 000 Ft',
    priceNote: '-12% a piaci ár alatt',
    priceNoteColor: 'text-[#22c55e]',
    area: 112,
    rooms: 3,
    baths: 2,
    img: '/galeria1_ai.jpg',
  },
  {
    id: 2,
    badge: 'ÚJ ÉPÍTÉSŰ',
    badgeColor: 'bg-[#3b82f6]',
    title: 'River Residence',
    location: 'Budapest, III. kerület',
    price: '64 500 000 Ft',
    priceNote: 'Új átadás: 2025 Q3',
    priceNoteColor: 'text-[#9ca3af]',
    area: 65,
    rooms: 2,
    baths: 1,
    img: '/galeria2_ai.jpg',
  },
  {
    id: 3,
    badge: 'BEFEKTETŐKNEK',
    badgeColor: 'bg-[#f59e0b]',
    title: 'Belvárosi lakás',
    location: 'Budapest, V. kerület',
    price: '59 900 000 Ft',
    priceNote: 'Magas bérleti hozam',
    priceNoteColor: 'text-[#f59e0b]',
    area: 45,
    rooms: 2,
    baths: 1,
    img: '/galeria3_ai.jpg',
  },
  {
    id: 4,
    badge: 'PRÉMIUM',
    badgeColor: 'bg-gradient-to-r from-purple-600 to-pink-600',
    title: 'Luxus villa',
    location: 'Budapest, II. kerület',
    price: '349 000 000 Ft',
    priceNote: 'Exkluzív, csendes környezet',
    priceNoteColor: 'text-[#9ca3af]',
    area: 245,
    rooms: 5,
    baths: 3,
    img: '/galeria4_ai.jpg',
  },
];

const NEW_PROJECTS = [
  { name: 'Greenwood Park',   location: 'Budapest, XI.  kerület', delivery: '2025 Q4', fill: 68, img: '/galeria1_ai.jpg' },
  { name: 'Lakeside Homes',   location: 'Budapest, XIII. kerület', delivery: '2026 Q2', fill: 45, img: '/galeria2_ai.jpg' },
  { name: 'Skyline Residence',location: 'Budapest, IX.  kerület', delivery: '2025 Q3', fill: 73, img: '/galeria3_ai.jpg' },
  { name: 'Harmony Garden',   location: 'Budapest, IV.  kerület', delivery: '2026 Q1', fill: 35, img: '/galeria4_ai.jpg' },
];

const SERVICES = [
  { icon: Bot,        label: 'AI Ingatlanértékelő', desc: 'Valós piaci ár számítás másodpercek alatt' },
  { icon: Banknote,   label: 'Hitel előminősítés',  desc: 'Gyors kalkuláció a legjobb ajánlatokkal' },
  { icon: Shield,     label: 'Jogi biztonság',       desc: 'Ellenőrzött ingatlanok, átlátható folyamat' },
  { icon: TrendingUp, label: 'Befektetési elemzés',  desc: 'Hozam, kockázat, megtérülés AI-val' },
  { icon: Eye,        label: '360° túra',            desc: 'Nézd meg otthonod virtuális sétával' },
];

const STATS = [
  { val: '500+',    label: 'Eladott ingatlan',    color: 'text-[#22c55e]' },
  { val: '1 200+',  label: 'Elégedett ügyfél',    color: 'text-[#3b82f6]' },
  { val: '30 nap',  label: 'Átlagos eladási idő', color: 'text-[#e31937]' },
  { val: '0 Ft',    label: 'Indulási költség',    color: 'text-[#f59e0b]' },
];

const PARTNERS = [
  'OTP Ingatlanpont', 'Duna House', 'BigGeorge Property', 'RICS', 'Magyar Bankholding', 'K&H Lakáshitel',
];

/* ─── Sub-components ─────────────────────────────────── */
function PropertyCard({ p }) {
  const [liked, setLiked] = useState(false);
  return (
    <div className="group bg-[#161616] border border-white/[0.07] rounded-2xl overflow-hidden hover:border-[#e31937]/30 hover:shadow-xl hover:shadow-black/40 transition-all duration-300 hover:-translate-y-1 flex-shrink-0 w-full">
      {/* Image */}
      <div className="relative h-44 overflow-hidden">
        <img
          src={p.img}
          alt={p.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=400&q=80'; }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <span className={`absolute top-3 left-3 ${p.badgeColor} text-white text-[10px] font-black px-2.5 py-1 rounded-full`}>
          {p.badge}
        </span>
        <button
          onClick={() => setLiked(!liked)}
          className="absolute top-3 right-3 w-7 h-7 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-[#e31937] transition-colors"
        >
          <Heart size={13} className={liked ? 'fill-white text-white' : 'text-white'} />
        </button>
      </div>
      {/* Body */}
      <div className="p-4">
        <h3 className="text-white font-bold text-sm mb-1 truncate">{p.title}</h3>
        <p className="text-[#9ca3af] text-xs mb-3 flex items-center gap-1">
          <MapPin size={11} /> {p.location}
        </p>
        <div className="flex items-center gap-3 text-[11px] text-[#6b7280] mb-3">
          <span className="flex items-center gap-1"><Square size={11} /> {p.area} m²</span>
          <span className="flex items-center gap-1"><Bed size={11} /> {p.rooms} szoba</span>
          <span className="flex items-center gap-1"><Bath size={11} /> {p.baths} fürdő</span>
        </div>
        <div className="flex items-end justify-between">
          <div>
            <div className="text-white font-black text-base">{p.price}</div>
            <div className={`text-[10px] font-medium ${p.priceNoteColor}`}>{p.priceNote}</div>
          </div>
          <Link
            to="/elado-ingatlanok"
            className="w-8 h-8 bg-[#e31937]/10 hover:bg-[#e31937] border border-[#e31937]/30 hover:border-[#e31937] rounded-xl flex items-center justify-center transition-all"
          >
            <ChevronRight size={14} className="text-[#e31937] group-hover:text-white" />
          </Link>
        </div>
      </div>
    </div>
  );
}

function SectionHeader({ title, linkTo, linkLabel = 'Összes megtekintése →' }) {
  return (
    <div className="flex items-center justify-between mb-5">
      <h2 className="text-white font-black text-lg">{title}</h2>
      {linkTo && (
        <Link to={linkTo} className="text-[#e31937] hover:text-white text-xs font-medium transition-colors flex items-center gap-1">
          {linkLabel} <ChevronRight size={13} />
        </Link>
      )}
    </div>
  );
}

/* ─── Main page ───────────────────────────────────────── */
const HOPPHomesHome = () => {
  const navigate = useNavigate();
  const [searchVal, setSearchVal] = useState('');

  return (
    <div className="min-h-screen bg-[#0a0a0a]">

      {/* ── HERO ──────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        {/* Background image */}
        <div className="absolute inset-0">
          <img
            src="/galeria1_ai.jpg"
            alt=""
            className="w-full h-full object-cover brightness-[0.25]"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/80 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
        </div>

        <div className="relative z-10 px-6 pt-12 pb-10 max-w-2xl">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight mb-4">
            Otthonok.<br />
            Befektetések.<br />
            <span className="text-[#e31937]">AI-val.</span>
          </h1>
          <p className="text-[#9ca3af] text-base mb-8 max-w-lg leading-relaxed">
            A HoppHomes AI asszisztenssel percek alatt megtalálod az ingatlant, ami valóban neked való.
          </p>

          {/* AI Search */}
          <div className="bg-[#161616]/90 backdrop-blur-xl border border-white/[0.1] rounded-2xl p-2 shadow-2xl shadow-black/50 mb-4">
            <div className="flex items-center gap-3 px-3 py-2.5">
              <Search size={18} className="text-[#e31937] flex-shrink-0" />
              <input
                value={searchVal}
                onChange={(e) => setSearchVal(e.target.value)}
                placeholder='Írj, amit keresel… pl. "Teraszos lakás Budán 80M Ft-ig"'
                className="flex-1 bg-transparent text-white text-sm placeholder-[#6b7280] outline-none"
              />
              <button
                onClick={() => navigate('/elado-ingatlanok')}
                className="bg-[#e31937] hover:bg-[#cc1530] text-white font-bold text-sm px-4 py-2 rounded-xl transition-colors flex-shrink-0"
              >
                Keresés
              </button>
            </div>
            {/* Filter pills */}
            <div className="flex items-center gap-2 px-3 pb-2 overflow-x-auto scrollbar-none">
              {[
                { icon: Home,      label: 'Típus',     sub: 'Lakás' },
                { icon: MapPin,    label: 'Helyszín',   sub: 'Bárfhol' },
                { icon: Banknote,  label: 'Ár',         sub: 'Max. 100M Ft' },
                { icon: Maximize2, label: 'Méret',      sub: 'Bármilyen' },
                { icon: DoorOpen,  label: 'Szobák',     sub: 'Bármely' },
              ].map((f) => {
                const Icon = f.icon;
                return (
                  <button
                    key={f.label}
                    className="flex items-center gap-1.5 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.06] rounded-xl px-3 py-1.5 flex-shrink-0 transition-colors group"
                  >
                    <Icon size={12} className="text-[#6b7280] group-hover:text-[#e31937] transition-colors" />
                    <span className="text-white text-[11px] font-medium">{f.label}</span>
                    <span className="text-[#6b7280] text-[11px]">{f.sub}</span>
                  </button>
                );
              })}
              <button className="flex items-center gap-1.5 bg-[#e31937]/10 border border-[#e31937]/20 rounded-xl px-3 py-1.5 flex-shrink-0 text-[#e31937] text-[11px] font-bold hover:bg-[#e31937]/20 transition-colors">
                <SlidersHorizontal size={12} />
                További szűrők
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURED LISTINGS ─────────────────────────── */}
      <section className="px-6 py-8">
        <SectionHeader title="Neked ajánljuk" linkTo="/elado-ingatlanok" linkLabel="Összes ajánlat megtekintése →" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {FEATURED.map((p) => <PropertyCard key={p.id} p={p} />)}
        </div>
      </section>

      {/* ── SERVICES ROW ──────────────────────────────── */}
      <section className="px-6 py-6">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {SERVICES.map((s) => {
            const Icon = s.icon;
            return (
              <div
                key={s.label}
                className="bg-[#161616] border border-white/[0.07] rounded-2xl p-4 hover:border-[#e31937]/30 hover:bg-[#1a1a1a] transition-all cursor-pointer group"
              >
                <div className="w-9 h-9 rounded-xl bg-[#e31937]/10 flex items-center justify-center mb-3 group-hover:bg-[#e31937]/20 transition-colors">
                  <Icon size={16} className="text-[#e31937]" />
                </div>
                <p className="text-white text-xs font-bold mb-1 leading-tight">{s.label}</p>
                <p className="text-[#6b7280] text-[10px] leading-relaxed">{s.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── NEW PROJECTS ──────────────────────────────── */}
      <section className="px-6 py-6">
        <SectionHeader title="Új építésű projektek" linkTo="/elado-ingatlanok" linkLabel="Összes projekt megtekintése →" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {NEW_PROJECTS.map((proj) => (
            <div
              key={proj.name}
              className="bg-[#161616] border border-white/[0.07] rounded-2xl overflow-hidden hover:border-[#e31937]/30 transition-all hover:-translate-y-0.5 group cursor-pointer"
            >
              <div className="relative h-28 overflow-hidden">
                <img
                  src={proj.img}
                  alt={proj.name}
                  className="w-full h-full object-cover brightness-75 group-hover:scale-105 transition-transform duration-500"
                  onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=300&q=70'; }}
                />
              </div>
              <div className="p-3">
                <p className="text-white font-bold text-xs truncate">{proj.name}</p>
                <p className="text-[#9ca3af] text-[10px] flex items-center gap-1 mb-2">
                  <MapPin size={9} /> {proj.location}
                </p>
                <p className="text-[#e31937] text-[10px] font-medium mb-2">Átadás: {proj.delivery}</p>
                <div className="w-full bg-white/[0.06] rounded-full h-1.5">
                  <div
                    className="bg-[#e31937] h-1.5 rounded-full transition-all"
                    style={{ width: `${proj.fill}%` }}
                  />
                </div>
                <p className="text-[#9ca3af] text-[10px] mt-1">Már {proj.fill}% elkelt</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── STATS ─────────────────────────────────────── */}
      <section className="px-6 py-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {STATS.map((s) => (
            <div
              key={s.label}
              className="bg-[#161616] border border-white/[0.07] rounded-2xl p-5 text-center hover:border-white/[0.12] transition-all"
            >
              <div className={`text-3xl font-black mb-1 ${s.color}`}>{s.val}</div>
              <p className="text-[#9ca3af] text-xs">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── HOT DEALS ─────────────────────────────────── */}
      <section className="px-6 py-4">
        <SectionHeader title="🔥 HOT DEALS" linkTo="/hot-deals" linkLabel="Összes deal →" />
        <HotDealsSection maxDeals={3} compact />
      </section>

      {/* ── PARTNER LOGOS ─────────────────────────────── */}
      <section className="px-6 py-8 border-t border-white/[0.05]">
        <p className="text-[#6b7280] text-xs text-center mb-5 uppercase tracking-widest font-medium">Partnereink</p>
        <div className="flex items-center justify-center flex-wrap gap-6">
          {PARTNERS.map((p) => (
            <span key={p} className="text-[#4b5563] text-sm font-medium hover:text-[#9ca3af] transition-colors cursor-pointer">
              {p}
            </span>
          ))}
        </div>
      </section>

      {/* ── AI CHAT (floating) ────────────────────────── */}
      <HOPPyAIChat />
    </div>
  );
};

export default HOPPHomesHome;
